import { EditIcon } from '@/assets/icons/edit';
import { RefreshIcon } from '@/assets/icons/info-icons/refresh';
import { SelectIcon } from '@/assets/icons/selectIcon';
import { Accordion } from '@/components/basicComponents/accordion';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import {
  QuickFilterSelect,
  QuickFilterTitle,
  QuickFilterWrapper,
  RefreshButton,
  Results,
  TopWrapper
} from '@/components/table/filterPage/desktop-filter';
import { TableColumns } from '@/components/table/types';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AssetClassForecastValueDto, CommitmentForecastValueDto, TransformedAssetClassForecastValueDto } from '../../../types';
import { getUniqueAssetClassColumnsWithDates, getUniqueCommitmentColumnsWithDates, groupAssetClasses } from '../../../utils';
import AssetClassTable from './assetClassTable/table';
import CommitmentTable from './commitmentTable/table';

interface CommitmentPlanSectionProps {
  assetClassNAVs: AssetClassForecastValueDto[];
  existingCommitmentNAVs: CommitmentForecastValueDto[];
  targetSubAssetClasses: boolean;
  commitmentPlanId: string | null;
  openOpportunityDetailsPage: (id: string) => void;
}

const VIEW_BY_SELECTS = ['Asset Class', 'Existing Commitments'];

export const NavForecastSection = ({
  assetClassNAVs,
  existingCommitmentNAVs,
  targetSubAssetClasses,
  commitmentPlanId,
  openOpportunityDetailsPage
}: CommitmentPlanSectionProps) => {
  const navigate = useNavigate();
  const params = useParams();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';

  const [viewBySelect, setViewBySelect] = useState('Asset Class');

  const [assetClassTableHeaderColumns, setAssetClassTableHeaderColumns] = useState<TableColumns[]>([]);
  const [assetClassColumnWidth, setAssetClassColumnWidth] = useState(0);
  const [assetClassTableData, setAssetClassTableData] = useState<TransformedAssetClassForecastValueDto[]>([]);

  const [commitmentTableHeaderColumns, setCommitmentTableHeaderColumns] = useState<TableColumns[]>([]);
  const [commitmentColumnWidth, setCommitmentColumnWidth] = useState(0);
  const [existingCommitmentTableData, setExistingCommitmentTableData] = useState<CommitmentForecastValueDto[]>([]);

  useEffect(() => {
    if (!assetClassNAVs) return;

    const tableColumns = getUniqueAssetClassColumnsWithDates(assetClassNAVs);
    const tableColumnsWithWidth = tableColumns.map((column) => ({ ...column, width: 85 / tableColumns.length }));
    setAssetClassColumnWidth(85 / tableColumns.length);
    setAssetClassTableHeaderColumns([{ title: 'Asset Class', key: null, width: 15, keySort: 'asset class' }, ...tableColumnsWithWidth]);

    const transformedData = groupAssetClasses(assetClassNAVs);
    const filteredTableData = transformedData.filter((data) => data.values.some((valueItem) => valueItem.value !== 0));

    setAssetClassTableData(filteredTableData);
  }, [assetClassNAVs]);

  useEffect(() => {
    if (!existingCommitmentNAVs) return;

    const tableColumnsDates = getUniqueCommitmentColumnsWithDates(existingCommitmentNAVs);

    const tableColumnsWithWidth = tableColumnsDates.map((column) => ({ ...column, width: 85 / tableColumnsDates.length }));
    setCommitmentColumnWidth(85 / tableColumnsDates.length);
    setCommitmentTableHeaderColumns([{ title: 'Commitment', key: null, width: 15, keySort: 'commitment' }, ...tableColumnsWithWidth]);

    const transformedData: CommitmentForecastValueDto[] = existingCommitmentNAVs.map((commitment) => {
      if (commitment.data.length === tableColumnsDates.length) {
        return { ...commitment, id: guid() };
      }

      const updatedData = tableColumnsDates.map((item) => {
        const findCommitmentData = commitment.data.find((dataItem) => dataItem.date === item.key);
        if (findCommitmentData) {
          return findCommitmentData;
        }
        return { date: item.key, value: null, actual: false };
      });

      return { ...commitment, id: guid(), data: updatedData };
    });

    setExistingCommitmentTableData(transformedData);
  }, [existingCommitmentNAVs]);

  return (
    <Accordion id="navForecast" title="NAV Forecast">
      <TableWrapper>
        <TopWrapper>
          {viewBySelect === 'Existing Commitments' && (
            <>
              <Results>{existingCommitmentNAVs.length} Results</Results>
              <RefreshButton onClick={() => {}}>
                <RefreshIcon width={15} height={15} fill={themeVar('action.primary')} />
              </RefreshButton>
            </>
          )}
          <QuickFilterWrapper padding={viewBySelect === 'Existing Commitments' ? '5px 10px 5px 30px' : '5px 0'}>
            <QuickFilterTitle>View By</QuickFilterTitle>
            <QuickFilterSelect
              value={viewBySelect}
              setValue={setViewBySelect}
              options={VIEW_BY_SELECTS.map((item) => createSimpleSelectOption(item))}
              centerList={true}
              size="sm"
              customArrowIcon={<SelectIcon fill={themeVar('font.action')} />}
            />
          </QuickFilterWrapper>
        </TopWrapper>
        {viewBySelect === 'Asset Class' && (
          <>
            {assetClassTableData.length ? (
              <AssetClassTable
                tableData={assetClassTableData}
                tableHeaderColumns={assetClassTableHeaderColumns}
                columnWidth={assetClassColumnWidth}
                loading={false}
                targetSubAssetClasses={targetSubAssetClasses}
              />
            ) : (
              <CommitmentPlanNoResults>
                No commitments are scheduled. This forecast is limited to existing commitments only. Edit the commitment plan to add future
                investments.
                {!commitmentPlanId || (
                  <EditCommitmentPlanButtonWrapper
                    onClick={() => {
                      navigate(
                        `/investors/${investorId}/${entityId}/editCommitmentPlan?commitmentPlanId=${commitmentPlanId}&backToTitle=All Commitment Plans`
                      );
                    }}
                  >
                    <EditIcon fill={themeVar('font.action')} />
                    <AddText>Edit Commitment Plan</AddText>
                  </EditCommitmentPlanButtonWrapper>
                )}
              </CommitmentPlanNoResults>
            )}
          </>
        )}
        {viewBySelect === 'Existing Commitments' && (
          <CommitmentTable
            tableData={existingCommitmentTableData}
            tableHeaderColumns={commitmentTableHeaderColumns}
            loading={false}
            columnWidth={commitmentColumnWidth}
            openOpportunityDetailsPage={openOpportunityDetailsPage}
          />
        )}
      </TableWrapper>
    </Accordion>
  );
};

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CommitmentPlanNoResults = styled.div`
  font-size: 18px;
  color: ${themeVar('font.base')};
`;

const EditCommitmentPlanButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const AddText = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${themeVar('font.action')};
`;
